import { h } from 'vue';
import { capitalize } from 'lodash';
import { DateTime, Info } from 'luxon';
import i18n, { getCurrentLocale } from '@/i18n';
import { LANG_ENGLISH, LANG_FRENCH } from '@/constants';
import { getEnvVar } from '@/utils/env';
import { replaceAll, getTextVariant } from '@/utils/stringUtils';

const currentLanguage = getCurrentLocale();
export function useComponentComposable() {
  const BASE_URL = getEnvVar('VITE_CDN_BASE_URL');
  const fallBackImage = `${BASE_URL}/assets/images/fall_back_image.jpg`;

  const renderHTMLContent = (e) => {
    const dataReplaceFirst = replaceAll(e.data, '&lt;', '<');
    return h(e.htmlElement || 'div', { class: e.class, innerHTML: replaceAll(dataReplaceFirst, '&gt;', '>') });
  };
  const displayDateTimeAlowLanguage = (date) => {
    const dayOfWeek = [
      { en: 'Monday', fr: 'lundi' },
      { en: 'Tuesday', fr: 'mardi' },
      { en: 'Wednesday', fr: 'mercredi' },
      { en: 'Thursday', fr: 'jeudi' },
      { en: 'Friday', fr: 'vendredi' },
      { en: 'Saturday', fr: 'samedi' },
      { en: 'Sunday', fr: 'dimanche' }
    ];

    const monthOfYear = [
      { en: 'January', fr: 'janvier' },
      { en: 'February', fr: 'février' },
      { en: 'March', fr: 'mars' },
      { en: 'April', fr: 'avril' },
      { en: 'May', fr: 'mai' },
      { en: 'June', fr: 'juin' },
      { en: 'July', fr: 'juillet' },
      { en: 'August', fr: 'août' },
      { en: 'September', fr: 'septembre' },
      { en: 'October', fr: 'octobre' },
      { en: 'November', fr: 'novembre' },
      { en: 'December', fr: 'décembre' }
    ];

    return `${dayOfWeek[DateTime.fromJSDate(new Date(date)).weekday - 1][i18n.global.locale.value]} ${i18n.global.locale.value === 'fr' ? 'le' : ''} ${DateTime.fromJSDate(new Date(date)).day} ${
      monthOfYear[DateTime.fromJSDate(new Date(date)).month - 1][i18n.global.locale.value]
    }${i18n.global.locale.value === 'fr' ? '' : ','} ${DateTime.fromJSDate(new Date(date)).year}`;
  };

  const numberDayInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

  const renderListMonth = (locale, display = 'long') => {
    return Info.months(display, {
      locale
    });
  };

  const renderListDay = (locale, display = 'long') => {
    return Info.weekdays(display, {
      locale
    });
  };

  const getFirstCharacterOfDay = (locale) => {
    return renderListDay(locale).map((dd) => dd[0]?.toUpperCase());
  };

  const displayDate = (day, month, locale, separate = '-') => {
    return day && month ? `${day}${separate}${renderListMonth(locale, 'short')[Number(month) - 1]}` : '';
  };

  const checkValidity = (seasonal) => {
    return !(!seasonal?.startMonth || !seasonal?.startDay || !seasonal?.endMonth || !seasonal?.endDay);
  };

  const checkEmptyPayload = (seasonalSummer, seasonalWinter, seasonalException) => {
    if (seasonalException.isDefaultSeason) {
      return checkValidity(seasonalSummer) && checkValidity(seasonalWinter);
    } else {
      return checkValidity(seasonalSummer) && checkValidity(seasonalWinter) && checkValidity(seasonalException);
    }
  };

  const handleCheckError = (seasonalSummer, seasonalWinter) => {
    if (!checkValidity(seasonalSummer) || !checkValidity(seasonalWinter)) {
      return false;
    }
    const matchSummer = checkMatchSeason(seasonalSummer, seasonalWinter);
    const matchWinter = checkMatchSeason(seasonalWinter, seasonalSummer);
    return matchSummer && matchWinter;
  };

  const isValidDate = (year, month, day) => {
    const date = new Date(year, month - 1, day);
    return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
  };

  const checkMatchSeason = (begin, target) => {
    const startDate = convertDateInYear(begin.startMonth, begin.startDay);
    let endDate = convertDateInYear(begin.endMonth, begin.endDay);
    if (startDate > endDate) {
      endDate = convertDateInNextYear(begin.endMonth, begin.endDay, false);
    }
    const targetTimeStart = convertDateInYear(target.startMonth, target.startDay);
    let targetTimeEnd = convertDateInYear(target.endMonth, target.endDay);
    if (targetTimeStart > targetTimeEnd) {
      targetTimeEnd = convertDateInNextYear(target.endMonth, target.endDay, false);
    }
    if (checkOverlapDate(begin, target) || convertDateInNextYear(begin.startMonth, begin.startDay, true) <= targetTimeEnd) {
      return false;
    }
    return !checkDateRange(startDate, endDate, targetTimeStart) && !checkDateRange(startDate, endDate, targetTimeEnd);
  };

  const checkOverlapDate = (begin, target) => {
    const startDate = convertDateInYear(begin.startMonth, begin.startDay);
    const endDate = convertDateInYear(begin.endMonth, begin.endDay);
    const targetTimeStart = convertDateInYear(target.startMonth, target.startDay);
    const targetTimeEnd = convertDateInYear(target.endMonth, target.endDay);

    const dates = [startDate, endDate, targetTimeStart, targetTimeEnd];
    const uniqueDates = new Set(dates.map((date) => date.getTime()));

    return uniqueDates.size < dates.length;
  };

  const convertDateInYear = (month, day) => {
    return new Date(new Date().getFullYear(), month - 1, day);
  };

  const convertDateInNextYear = (month, day, isNotCheck = false) => {
    const isCheckDate = isValidDate(new Date().getFullYear() + 1, +month, +day);
    if (!isCheckDate && !isNotCheck) {
      return new Date(new Date().getFullYear() + 1, month - 1, day - 1);
    } else {
      return new Date(new Date().getFullYear() + 1, month - 1, day);
    }
  };

  const checkDateRange = (startTime, endTime, targetTime) => {
    return targetTime >= startTime && targetTime <= endTime;
  };

  const checkValidateListDateRange = (listDate = []) => {
    return listDate.length === 0 ? true : listDate.every((date) => date?.start && date?.end);
  };

  const renderLanguages = () => {
    return [
      {
        value: LANG_ENGLISH,
        label: i18n.global.t('common.english')
      },
      {
        value: LANG_FRENCH,
        label: i18n.global.t('common.french')
      }
    ];
  };

  const areTextVariantsEqual = (firstTextEn: string, firstTextFr: string, secondTextEn: string, secondTextFr: string) => {
    return getTextVariant(currentLanguage, firstTextEn, firstTextFr) === getTextVariant(currentLanguage, secondTextEn, secondTextFr);
  };

  const groupedByCountry = (data: any) => {
    const groupedArray: any = [];
    data.forEach((item: any) => {
      const countryGroup = groupedArray.find((group: any) => areTextVariantsEqual(group[0].destCountryEn, group[0].destCountryFr, item.destCountryEn, item.destCountryFr));
      if (countryGroup) {
        const isDuplicate = countryGroup.some(
          (existingItem: any) =>
            existingItem.destCode === item.destCode &&
            areTextVariantsEqual(existingItem.cityNameEn, existingItem.cityNameFr, item.cityNameEn, item.cityNameFr) &&
            areTextVariantsEqual(existingItem.destNameEn, existingItem.destNameFr, item.destNameEn, item.destNameFr)
        );
        if (!isDuplicate) {
          countryGroup.push(item);
        }
      } else {
        groupedArray.push([item]);
      }
    });
    return groupedArray;
  };

  const updateDataCity = (listCity: object[]) => {
    const newList = groupedByCountry(listCity);
    newList.forEach((value: any) => {
      value.sort((first: any, second: any) => first[`destName${capitalize(currentLanguage)}`].localeCompare(second[`destName${capitalize(currentLanguage)}`]));
    });
    return newList.map((group: any) => {
      const destCodes = group.map((item: any) => item.destCode).join(',');
      const destId = group.map((item: any) => item.destId).join(',');
      const destCountry = getTextVariant(currentLanguage, group[0].destCountryEn, group[0].destCountryFr);
      const cityName = group.map((item: any) => item[`cityName${capitalize(currentLanguage)}`]).join(', ');
      const region = group.map((item: any) => item[`region${capitalize(currentLanguage)}`]).join(', ');
      const referenceType = group.map((item: any) => item.referenceType).join(', ');
      const summaryObject = {
        destCode: destCodes,
        destId: destId,
        brochure: group[0].brochure,
        [`destName${capitalize(currentLanguage)}`]: destCountry,
        [`destCountry${capitalize(currentLanguage)}`]: destCountry,
        destCountryParent: destCountry,
        referenceType: referenceType,
        [`cityName${capitalize(currentLanguage)}`]: cityName,
        [`region${capitalize(currentLanguage)}`]: region
      };
      return [summaryObject, ...group];
    });
  };

  const formatCountryFromCity = (listCity: object[]) => {
    if (listCity.length) {
      const cityArray = updateDataCity(listCity);
      cityArray.sort((firstVal: any, secondVal: any) => {
        const firstCountry = firstVal[0].destCountryParent || '';
        const secondCountry = secondVal[0].destCountryParent || '';
        if (firstCountry < secondCountry) {
          return -1;
        }
        if (firstCountry > secondCountry) {
          return 1;
        }
        return 0;
      });
      return cityArray.reduce((accumulator: any, current: any) => accumulator.concat(current), []);
    } else {
      return listCity;
    }
  };

  return {
    renderHTMLContent,
    displayDateTimeAlowLanguage,
    fallBackImage,
    renderListMonth,
    renderListDay,
    getFirstCharacterOfDay,
    numberDayInMonth,
    displayDate,
    handleCheckError,
    checkValidity,
    checkEmptyPayload,
    convertDateInNextYear,
    convertDateInYear,
    checkValidateListDateRange,
    checkOverlapDate,
    renderLanguages,
    formatCountryFromCity
  };
}
